/* src/components/Dashboard.js */
import React, { useMemo, useState, useEffect } from 'react';
import TodaysStudyPlanWidget from './TodaysStudyPlanWidget';
import ContactModal from './ContactModal'; 
import LegalCommandWidget from './LegalCommandWidget';
import './Dashboard.css';
import { getLocalDateString, subjectMap } from '../constants';
import OnboardingTooltip from './OnboardingTooltip';
import { getDashboardMetrics } from '../storageService'; // Import the new getter

function Dashboard({ 
    onStartPhase, docket, handleRecalculateCurriculum, 
    userGoals, allItemsMap, userAccessLevel, rulesProgress, setView,
    userData, reviewCount, fastTrackCount, onOpenHistoryModal, 
    availableQuizCount, onOpenUpgrade,  onOpenContact, onReportIssue, seenHints, onDismissHint
}) {
    const [isContactModalOpen, setIsContactModalOpen] = useState(false);
    const [isOutlinesModalOpen, setIsOutlinesModalOpen] = useState(false);
    
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
    
    // Future-proofing: Local state for metrics if we stop passing rulesProgress
    const [masteryCount, setMasteryCount] = useState(0);

    const isFree = userAccessLevel === 'free';

    // --- NEW: Completion Detection ---
    const isDocketCleared = useMemo(() => {
        if (!docket?.schedule) return false;
        const todayStr = getLocalDateString(new Date());
        const day = docket.schedule.find(d => d.date === todayStr);
        if (!day || day.is_break_day) return false;

        const learnedToday = day.metrics?.learned_count || 0;
        const quizToday = day.metrics?.quiz_count || 0;
        const learnTarget = day.targets?.learn || 0;
        const quizTarget = day.targets?.quiz || 0;

        // All items cleared: De Novo (reviews + fast track) AND In Limine AND Quizzes
        return (reviewCount + fastTrackCount) === 0 && 
               learnedToday >= learnTarget && 
               quizToday >= quizTarget;
    }, [docket, reviewCount, fastTrackCount]);

    const todaysDocketDay = useMemo(() => {
        if (!docket?.schedule) return null;
        const todayStr = getLocalDateString(new Date());
        return docket.schedule.find(day => day.date === todayStr);
    }, [docket]);

    // OPTIMIZATION: Calculate metrics locally for now (instant), but structure allows async later
    const velocityMetrics = useMemo(() => {
        if (!userGoals) return { target: 0, progress: 0 };
        const target = todaysDocketDay?.targets?.learn || userGoals.daily_rule_target || 0;
        
        // Note: In the future, this total could be a count from canonical DB
        const total = [...allItemsMap.values()].filter(i => i.itemType === 'rule').length;
        
        // Current: Use props. Future: Use 'masteryCount' state fetched via SQL
        const currentMastered = Object.values(rulesProgress || {}).filter(p => p.srs_data?.learningStepIndex === -1).length;
        
        const progress = total > 0 ? Math.round((currentMastered / total) * 100) : 0;
        return { target, progress };
    }, [todaysDocketDay, userGoals, allItemsMap, rulesProgress]);

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        if (searchTerm.trim()) {
            setIsSearchModalOpen(true);
        }
    };

    const handleResultClick = (item) => {
        if (isFree && !item.is_free_preview) {
            onOpenUpgrade();
            return;
        }
        setIsSearchModalOpen(false);
        setSearchTerm('');
        onOpenHistoryModal(item.id);
    };

    // ... Search Results Memo (unchanged) ...
    const searchResults = useMemo(() => {
        const query = searchTerm.toLowerCase().trim();
        if (!query) return [];
        const tokens = query.split(/\s+/);
        const results = [];
        allItemsMap.forEach((item) => {
            if (item.itemType !== 'rule') return;
            const keynote = rulesProgress?.[item.id]?.keynote?.toLowerCase() || '';
            const name = item.ruleName.toLowerCase();
            const content = item.content_plain?.toLowerCase() || '';
            const searchStack = `${name} ${keynote} ${content}`;
            if (tokens.every(token => searchStack.includes(token))) {
                results.push(item);
            }
        });
        return results.sort((a, b) => a.ruleName.length - b.ruleName.length).slice(0, 15);
    }, [searchTerm, allItemsMap, rulesProgress]);

    return (
        <section className="dashboard-container theme-aware">
            <ContactModal isOpen={isContactModalOpen} onClose={() => setIsContactModalOpen(false)} />
            
            {isFree && (
                <div className="free-user-demo-banner">
                    <div className="banner-message">
                        <span className="material-symbols-outlined" style={{fontSize: '1.5em'}}>biotech</span>
                        <span><strong>Demo Mode:</strong> You are exploring a trial version. Upgrade to unlock the full adaptive schedule.</span>
                    </div>
                    <button className="btn upgrade-btn-mini" onClick={onOpenUpgrade}>
                        Upgrade Now
                    </button>
                </div>
            )}

            <div className="dashboard-utility-bar">
                <div className="search-wrapper">
                    <OnboardingTooltip id="hint_dashboard_search" seenHints={seenHints} onDismiss={onDismissHint} position="bottom" text="Search by keyword or rule name.">
                        <form className="search-box-full" onSubmit={handleSearchSubmit}>
                            <input type="text" className="search-input" placeholder="Search rules or keynotes..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}/>
                            <button type="submit" className="search-btn"><span className="material-symbols-outlined">search</span></button>
                        </form>
                    </OnboardingTooltip>
                </div>
                <button className="utility-btn" onClick={() => setIsOutlinesModalOpen(true)}><span className="material-symbols-outlined">folder_open</span>My Outlines</button>
                <button className="utility-btn" onClick={onOpenContact}><span className="material-symbols-outlined">help</span>Help / Ask Wali</button>
            </div>

            <OnboardingTooltip id="hint_daily_docket" seenHints={seenHints} onDismiss={onDismissHint} position="top" text="This is your brain's prescription for today.">
            <div className="full-width-docket-container">
                {isDocketCleared && (
                    <div className="docket-complete-banner fade-in">
                        <span className="material-symbols-outlined">verified</span>
                        <div className="banner-text">
                            <strong>Congratulations!</strong> You have completed today's tasks. Keep it up and you will complete the curriculum on schedule.
                        </div>
                    </div>
                )}
                <TodaysStudyPlanWidget
                    docket={docket}
                    dayData={todaysDocketDay}
                    reviewCount={reviewCount}
                    fastTrackCount={fastTrackCount}
                    velocity={velocityMetrics}
                    onStartSession={onStartPhase}
                    onRecalculateCurriculum={handleRecalculateCurriculum}
                    availableQuizCount={availableQuizCount}
                    onViewPlanner={() => setView('planner')}
                    onViewDeNovo={() => setView('deNovo')}
                    userData={userData}
                />
            </div>
            </OnboardingTooltip>

            {isOutlinesModalOpen && (
                <div className="modal-overlay active" onClick={() => setIsOutlinesModalOpen(false)}>
                    <div className="command-center-modal brutalist-modal theme-aware" onClick={e => e.stopPropagation()}>
                        <header className="modal-header"><h2 className="modal-title">My Outlines</h2><button className="close-btn" onClick={() => setIsOutlinesModalOpen(false)}>&times;</button></header>
                        <div className="modal-body">
                            <LegalCommandWidget allItemsMap={allItemsMap} rulesProgress={rulesProgress} onOpenRule={(id) => { onOpenHistoryModal(id); setIsOutlinesModalOpen(false); }} userName={userData?.profile?.name || 'Student'}/>
                        </div>
                    </div>
                </div>
            )}

            {isSearchModalOpen && (
                <div className="modal-overlay active" onClick={() => setIsSearchModalOpen(false)}>
                    <div className="command-center-modal brutalist-modal theme-aware" onClick={e => e.stopPropagation()}>
                        <header className="modal-header"><h2 className="modal-title">Search Results</h2><button className="close-btn" onClick={() => setIsSearchModalOpen(false)}>&times;</button></header>
                        <div className="modal-body">
                            {searchResults.length > 0 ? (
                                searchResults.map(res => {
                                    const isLocked = isFree && !res.is_free_preview;
                                    return (<div key={res.id} className={`result-item ${isLocked ? 'locked' : ''}`} onClick={() => handleResultClick(res)}><span className="result-path">{subjectMap[res.subject] || res.subject} › {res.displayPath?.join(' › ')}</span><h3 className="keynote-rule-name">{res.ruleName}{isLocked && <span className="material-symbols-outlined lock-icon-sm" style={{fontSize: '0.9em', marginLeft: '8px', verticalAlign: 'middle', opacity: 0.6}}>lock</span>}</h3></div>);
                                })
                            ) : (<div className="no-results">No matches found for "{searchTerm}"</div>)}
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}

export default Dashboard;